import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase.ts';
import { getCleanAvatarUrl } from '../utils/avatar.ts';

interface ProfileContextType {
  avatarUrl: string;
  avatarVersion: number;
  updateAvatarUrl: (newUrl: string) => void;
  refreshProfile: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType>({
  avatarUrl: '',
  avatarVersion: Date.now(),
  updateAvatarUrl: () => {},
  refreshProfile: async () => {},
});

interface ProfileProviderProps {
  children: React.ReactNode;
  userUid?: string;
  initialAvatarUrl?: string;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({
  children,
  userUid,
  initialAvatarUrl = '',
}) => {
  const [avatarUrl, setAvatarState] = useState<string>(getCleanAvatarUrl(initialAvatarUrl));
  const [avatarVersion, setAvatarVersion] = useState<number>(Date.now());

  // Update avatar URL across all components
  const updateAvatarUrl = useCallback((newUrl: string) => {
    const clean = getCleanAvatarUrl(newUrl);
    if (clean) {
      setAvatarState(clean);
      setAvatarVersion(Date.now());
    }
  }, []);

  // Sync when initialAvatarUrl changes from parent if non-empty
  useEffect(() => {
    if (initialAvatarUrl) {
      const clean = getCleanAvatarUrl(initialAvatarUrl);
      if (clean && clean !== avatarUrl) {
        setAvatarState(clean);
        setAvatarVersion(Date.now());
      }
    }
  }, [initialAvatarUrl]);

  // Re-fetch avatar from Supabase profiles table after upload or auth change
  const refreshProfile = useCallback(async () => {
    if (!userUid) return;
    try {
      // 1. Fetch from Supabase profiles table
      const { data, error } = await supabase
        .from('profiles')
        .select('avatar_url')
        .or(`uid.eq.${userUid},user_id.eq.${userUid}`)
        .maybeSingle();

      if (!error && data?.avatar_url) {
        const clean = getCleanAvatarUrl(data.avatar_url);
        if (clean) {
          setAvatarState(clean);
          setAvatarVersion(Date.now());
          console.log('[ProfileContext] Profile refreshed from Supabase:', clean);
          return;
        }
      }

      // 2. Fetch from backend API
      const res = await fetch(`/api/bootstrap?uid=${encodeURIComponent(userUid)}`);
      if (res.ok) {
        const boot = await res.json();
        const apiAvatar = boot?.account?.user?.avatarUrl || boot?.account?.profile?.avatarUrl;
        if (apiAvatar) {
          const clean = getCleanAvatarUrl(apiAvatar);
          if (clean) {
            setAvatarState(clean);
            setAvatarVersion(Date.now());
            console.log('[ProfileContext] Profile refreshed from API:', clean);
          }
        }
      }
    } catch (e) {
      console.warn('[ProfileContext] Failed to refresh profile:', e);
    }
  }, [userUid]);

  return (
    <ProfileContext.Provider
      value={{
        avatarUrl,
        avatarVersion,
        updateAvatarUrl,
        refreshProfile,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => useContext(ProfileContext);
